import { ReactNode } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { HourglassEmpty as HourglassEmptyIcon } from '@mui/icons-material';
import { useLanguage } from '../../../../context/LanguageContext';
import type { QueryResultResponse, QueryResultStatus } from '../../../../hooks/QueryBuilder/useQueryResult';
import CountCards from './CountCards';
import PersonIdsTable from './PersonIdsTable';
import TotalCountBanner from './TotalCountBanner';

const RESULT_KEY = 'pages.clients.queryBuilder.resultDialog';

interface QueryResultBodyProps {
    starting: boolean;
    loading: boolean;
    error: string | null;
    result: QueryResultResponse | null;
    status: QueryResultStatus;
}

function CenteredLoader({ children }: { children: ReactNode }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, gap: 2, textAlign: 'center' }}>
            <CircularProgress />
            {children}
        </Box>
    );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>{title}</Typography>
            {children}
        </Box>
    );
}

function FailedAlert({ message }: { message?: string }) {
    const { t } = useLanguage();
    const isInvalidSql = !!message?.includes('bad SQL grammar');

    return (
        <Alert severity="error">
            <strong>{t(`${RESULT_KEY}.failedTitle`)}</strong>
            {' '}
            {isInvalidSql ? t(`${RESULT_KEY}.invalidSqlMessage`) : message || t(`${RESULT_KEY}.genericErrorMessage`)}
            {isInvalidSql && (
                <Box component="pre" sx={{ mt: 1, fontSize: '0.7rem', whiteSpace: 'pre-wrap', opacity: 0.7 }}>{message}</Box>
            )}
        </Alert>
    );
}

function QueryResultBody({ starting, loading, error, result, status }: QueryResultBodyProps) {
    const { t, formatLocale } = useLanguage();
    const isCompleted = status === 'completed';
    // Un seul affichage « comptage en cours » du lancement jusqu'au résultat, pour éviter que le contenu saute
    const counting = (starting && !result) || status === 'pending';

    return (
        <>
            {counting && (
                <CenteredLoader>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t(`${RESULT_KEY}.countingTitle`)}</Typography>
                    <Typography variant="body2" color="text.secondary">{t(`${RESULT_KEY}.countingMessage`)}</Typography>
                </CenteredLoader>
            )}

            {!counting && loading && !result && (
                <CenteredLoader>
                    <Typography variant="body2" color="text.secondary">{t(`${RESULT_KEY}.loading`)}</Typography>
                </CenteredLoader>
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {result && !counting && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {status === 'expired' && (
                        <Alert severity="warning" icon={<HourglassEmptyIcon />}>{t(`${RESULT_KEY}.expiredMessage`)}</Alert>
                    )}

                    {status === 'error' && <FailedAlert message={result.message} />}

                    {isCompleted && typeof result.count === 'number' && (
                        <TotalCountBanner count={result.count} label={t(`${RESULT_KEY}.globalCount`)} />
                    )}

                    {isCompleted && !!result.listCount?.length && (
                        <Section title={t(`${RESULT_KEY}.channelBreakdown`)}>
                            <CountCards listCount={result.listCount} total={result.count} />
                        </Section>
                    )}

                    {isCompleted && !!result.personIds?.length && (
                        <Section title={t(`${RESULT_KEY}.personIdsList`, { count: result.personIds.length.toLocaleString(formatLocale) })}>
                            <PersonIdsTable personIds={result.personIds} />
                        </Section>
                    )}
                </Box>
            )}
        </>
    );
}

export default QueryResultBody;
